import { useEffect, useState } from "react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ShieldCheck, AlertTriangle, CheckCircle2,
  TrendingUp, ArrowRight,
} from "lucide-react";

// AICreditMemoModal — admin-facing AI credit memo for a single buyer.
// Pulls the latest cached memo and offers a "Re-generate" button that hits
// /api/admin/credit/memo/{buyer_id} (Claude Sonnet 4.5 via Emergent LLM key).
//
// Usage:
//   const [openFor, setOpenFor] = useState(null);
//   <AICreditMemoModal buyer={openFor} onClose={() => setOpenFor(null)}/>
//   // open by passing a buyer object: setOpenFor(buyer)
export default function AICreditMemoModal({ buyer, onClose }) {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const open = !!buyer;

  useEffect(() => {
    if (!buyer) return;
    setLoading(true);
    api.get(`/admin/credit/memo/${buyer.id}`)
      .then(r => setMemos(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [buyer]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/credit/memo/${buyer.id}`);
      setMemos(prev => [data, ...prev]);
      toast.success(`AI memo generated · ${(data.confidence * 100).toFixed(0)}% confidence`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate memo");
    } finally {
      setGenerating(false);
    }
  };

  const latest = memos[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="ai-memo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 display">
            <Sparkles className="w-5 h-5 text-accent"/> AI Credit Memo
            <span className="text-foreground/50 font-normal text-sm">· {buyer?.company || buyer?.name}</span>
          </DialogTitle>
        </DialogHeader>

        {!latest && !loading && (
          <div className="py-6 text-center">
            <div className="text-sm text-foreground/65 mb-4">
              No memo yet. Generate one — Verqo AI reads the buyer's KYB + Plaid + profile and writes a structured underwriting memo in ~5 seconds.
            </div>
            <Button onClick={generate} disabled={generating} data-testid="ai-memo-generate"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5">
              {generating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Underwriting…</> : <><Sparkles className="w-4 h-4 mr-1.5"/>Generate AI memo</>}
            </Button>
          </div>
        )}

        {loading && <div className="py-10 text-center text-foreground/55"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>}

        {latest && <MemoView memo={latest} onRegenerate={generate} regenerating={generating}/>}
      </DialogContent>
    </Dialog>
  );
}

// Read-only memo view (also reusable inline)
export function MemoView({ memo, onRegenerate, regenerating }) {
  const dec = memo.recommended_decision;
  const tone = dec === "approve"
    ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", chip: "bg-emerald-600 text-white", Icon: CheckCircle2, label: "APPROVE" }
    : dec === "decline"
    ? { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", chip: "bg-rose-600 text-white", Icon: AlertTriangle, label: "DECLINE" }
    : { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", chip: "bg-amber-600 text-white", Icon: ShieldCheck, label: "MANUAL REVIEW" };

  return (
    <div className="space-y-4" data-testid="ai-memo-view">
      <div className={`rounded-2xl border p-5 ${tone.bg} ${tone.border} ${tone.text}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">
            <tone.Icon className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`chip text-[10.5px] mono uppercase tracking-widest ${tone.chip}`} data-testid="ai-memo-decision">
                {tone.label}
              </span>
              <span className="text-[11px] mono opacity-70">
                {(memo.confidence * 100).toFixed(0)}% confidence
              </span>
              <span className="text-[10px] mono opacity-50">· {memo.model}</span>
            </div>
            <p className="text-[13.5px] mt-3 leading-relaxed">{memo.memo}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        <Tile l="Limit"     v={memo.recommended_credit_limit_usd ? money(memo.recommended_credit_limit_usd) : "—"}/>
        <Tile l="Terms"     v={(memo.recommended_terms || "").replace("net", "Net-").replace("wallet_only", "Wallet only")}/>
        <Tile l="Generated" v={new Date(memo.generated_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}/>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <BulletList title="Strengths" tone="positive" items={memo.key_strengths}/>
        <BulletList title="Risks"     tone="negative" items={memo.key_risks}/>
      </div>

      {onRegenerate && (
        <div className="flex justify-between items-center pt-2 border-t border-border">
          <div className="text-[11px] text-foreground/45">
            Re-generate after the buyer connects their bank or KYB updates.
          </div>
          <Button onClick={onRegenerate} disabled={regenerating} variant="outline" size="sm"
            data-testid="ai-memo-regenerate" className="rounded-full text-xs h-8">
            {regenerating ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin"/>Re-running…</> : <><Sparkles className="w-3 h-3 mr-1.5"/>Re-generate</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function Tile({ l, v }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3">
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{l}</div>
      <div className="display text-base mt-1">{v}</div>
    </div>
  );
}

function BulletList({ title, tone, items }) {
  const c = tone === "positive" ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">{title}</div>
      {(items || []).length === 0
        ? <div className="text-xs text-foreground/45">None identified</div>
        : <ul className="space-y-1.5">
            {items.map((it, i) => (
              <li key={i} className="text-[12.5px] flex items-start gap-2">
                <ArrowRight className={`w-3 h-3 mt-1 shrink-0 ${c}`}/>
                <span className="leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>}
    </div>
  );
}
